import { Trash2 } from 'lucide-react';
import { FormattedDate } from 'react-intl';

import { useIntl } from '../../hooks/useIntl.js';
import { formatFileSize } from '../../lib/number-format.js';
import type { Firmware } from '../../types/firmware.js';
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui';

interface FirmwaresTableProps {
  firmwares: Firmware[];
  onDelete: (firmware: Firmware) => void;
}

export function FirmwaresTable({ firmwares, onDelete }: FirmwaresTableProps) {
  const intl = useIntl();

  if (!firmwares.length) {
    return (
      <div className="text-sm text-foreground">
        {intl.formatMessage({ id: 'firmwares.table.noFirmwares' })}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader className="bg-background-light">
        <TableRow>
          <TableHead>{intl.formatMessage({ id: 'firmwares.table.version' })}</TableHead>
          <TableHead>{intl.formatMessage({ id: 'firmwares.table.fileName' })}</TableHead>
          <TableHead className="w-24">
            {intl.formatMessage({ id: 'firmwares.table.fileSize' })}
          </TableHead>
          <TableHead>{intl.formatMessage({ id: 'firmwares.table.deviceModels' })}</TableHead>
          <TableHead className="w-24 text-center">
            {intl.formatMessage({ id: 'firmwares.table.devicesCount' })}
          </TableHead>
          <TableHead className="w-48">
            {intl.formatMessage({ id: 'firmwares.table.createdAt' })}
          </TableHead>
          <TableHead className="w-20 text-right">
            {intl.formatMessage({ id: 'firmwares.table.actions' })}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {firmwares.map((firmware) => (
          <TableRow key={firmware.id}>
            <TableCell className="font-mono text-sm font-medium">{firmware.version}</TableCell>
            <TableCell className="text-sm truncate max-w-[200px]" title={firmware.fileName}>
              {firmware.fileName}
            </TableCell>
            <TableCell className="text-sm text-foreground">
              {formatFileSize(firmware.fileSize)}
            </TableCell>
            <TableCell>
              {firmware.deviceModels.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {firmware.deviceModels.map((model) => (
                    <Badge key={model} variant="secondary" className="text-xs">
                      {model}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-xs text-foreground">—</span>
              )}
            </TableCell>
            <TableCell className="text-center text-sm">{firmware.devicesCount}</TableCell>
            <TableCell className="text-sm text-foreground">
              <FormattedDate
                value={new Date(firmware.createdAt)}
                year="numeric"
                month="numeric"
                day="numeric"
                hour="2-digit"
                minute="2-digit"
              />
            </TableCell>
            <TableCell>
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(firmware)}
                  title={intl.formatMessage({ id: 'firmwares.table.deleteFirmware' })}
                  data-testid="delete-firmware-button"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
